import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { App } from './app/App';
import { queryClient } from './app/queryClient';
import { router } from './app/router';
import { setUnauthorizedHandler } from './shared/api/client';
import { CURRENT_USER_KEY } from './features/auth/useCurrentUser';
import { PERMISSIONS_KEY } from './features/auth/usePermissions';
import { useCurrentWarehouseStore } from './features/warehouses/currentWarehouse';
import { loadRuntimeConfig } from './shared/config/runtime';
import { registerServiceWorker } from './sw/registerSW';
import { startAutoVersionCheck } from './shared/versionCheck';
import { initInstallPromptCapture } from './shared/hooks/usePwaInstall';
import './shared/i18n';

const container = document.getElementById('root');
if (!container) {
  throw new Error('Root container #root not found in index.html');
}

const root = createRoot(container);

// Arm this before the awaited config fetch below: the browser fires
// `beforeinstallprompt` once and early, and the shell's Install button only
// mounts after login — so the event has to be captured process-wide up front.
initInstallPromptCapture();

// Bootstrap: fetch /config.json before mounting React so api/client.ts and
// every feature module can call getRuntimeConfig() synchronously. In dev,
// /config.json is served from /public; in prod, the IIS deploy procedure
// edits the same file to flip Stage ↔ Production without rebuilding.
try {
  await loadRuntimeConfig();
} catch (err) {
  // Show a minimal fallback so a missing or malformed config.json isn't a
  // silent white-screen.
  root.render(
    <div style={{ padding: 24, fontFamily: 'system-ui, sans-serif' }}>
      <h1 style={{ margin: 0, fontSize: 20 }}>InveTrak — startup failed</h1>
      <p style={{ color: '#666' }}>
        Could not load runtime configuration. Check that <code>/config.json</code> exists
        and is readable.
      </p>
      <pre style={{ background: '#f6f6f6', padding: 12, fontSize: 12 }}>
        {(err as Error).message}
      </pre>
    </div>,
  );
  throw err;
}

// Session-expiry handler: a 401 on any business call clears the cached user
// and bounces to /login (the auth endpoints are exempt — see shared/api/client).
setUnauthorizedHandler(() => {
  queryClient.setQueryData(CURRENT_USER_KEY, null);
  // The session is gone, so the permissions it carried are meaningless. Dropped here as well
  // as on sign-out, because an expiry never reaches the sign-out path.
  queryClient.removeQueries({ queryKey: PERMISSIONS_KEY });
  // Same reason as sign-out, and an expiry never reaches that path.
  useCurrentWarehouseStore.getState().setWarehouse(null);
  if (router.state.location.pathname !== '/login') {
    void router.navigate({ to: '/login' });
  }
});

root.render(
  <StrictMode>
    <App />
  </StrictMode>,
);

registerServiceWorker();
startAutoVersionCheck();
